import axios from 'axios';
import LogRocket from 'logrocket';
import store from '@/store';

let environmentSuffix = 'development';
if (process.env.VUE_APP_MODE === 'production') environmentSuffix = 'production';

const isDev = process.env.VUE_APP_MODE === 'development' || process.env.NODE_ENV === 'development';

/**
 * Where the backend lives. VUE_APP_API_BASE overrides it, which is the only way to run this
 * app against a backend on localhost — without it, `yarn dev:serve` always talks to the
 * deployed dev App Engine service and a backend change cannot be tried out before it ships.
 * Unset (every build we deploy) it resolves to exactly the URL it always did.
 */
const apiBase = process.env.VUE_APP_API_BASE || `https://eventor-${environmentSuffix}.appspot.com`;

const createEventURL = `${apiBase}/createEvent`;
const getUpgradeEventSessionURL = `${apiBase}/upgradeEvent`;
const addSubscriptionURL = `${apiBase}/addSubscription`;
const addTeamMemberURL = `${apiBase}/addTeamMember`;
const updateTeamMemberURL = `${apiBase}/updateTeamMember`;
const resetTeamMemberPasswordURL = `${apiBase}/resetTeamMemberPassword`;
const removeTeamMemberURL = `${apiBase}/deleteTeamMember`;
const refreshTeamMembersStatusURL = `${apiBase}/refreshTeamMembersStatus`;
const inviteInfoURL = `${apiBase}/inviteInfo`;
const acceptInviteURL = `${apiBase}/acceptInvite`;
const resendTeamInviteURL = `${apiBase}/resendTeamInvite`;
const intercomTokenURL = `${apiBase}/intercomToken`;
const passkeyURL = (action) => `${apiBase}/passkeys/${action}`;

const defaultConfig = {
  headers: {
    'Content-Type': 'application/json',
  },
  responseType: 'json',
};

async function getRequestConfig() {
  const requestConfig = defaultConfig;
  const authToken = await store.dispatch('user/auth/getAuthToken');

  requestConfig.headers.Authorization = `Bearer ${authToken}`;

  return requestConfig;
}

export async function APICreateEvent(requestData) {
  const requestConfig = await getRequestConfig();

  if (isDev) console.info('# APICreateEvent', requestData);
  else LogRocket.info('# APICreateEvent', requestData);

  return axios.post(createEventURL, requestData, requestConfig);
}

export async function APIGetUpgradeEventSession(requestData) {
  const requestConfig = await getRequestConfig();

  if (isDev) console.info('# APIGetUpgradeEventSession', requestData);
  else LogRocket.info('# APIGetUpgradeEventSession', requestData);

  return axios.post(getUpgradeEventSessionURL, requestData, requestConfig);
}

export async function APIAddSubscription(requestData) {
  const requestConfig = await getRequestConfig();

  if (isDev) console.info('# APIAddSubscription', requestData);
  else LogRocket.info('# APIAddSubscription', requestData);

  return axios.post(addSubscriptionURL, requestData, requestConfig);
}

export async function APIAddTeamMember(requestData) {
  const requestConfig = await getRequestConfig();

  if (isDev) console.info('# APIAddTeamMember', requestData);
  else LogRocket.info('# APIAddTeamMember', requestData);

  return axios.post(addTeamMemberURL, requestData, requestConfig);
}

export async function APIUpdateTeamMember(requestData) {
  const requestConfig = await getRequestConfig();

  if (isDev) console.info('# APIUpdateTeamMember', requestData);
  else LogRocket.info('# APIUpdateTeamMember', requestData);

  return axios.post(updateTeamMemberURL, requestData, requestConfig);
}

/**
 * Issues a new single-use password for a team member. The password comes back in the
 * response and is not stored anywhere, so the caller must show it to the owner there and
 * then. Only the request is logged — never the response, which carries the credential.
 */
export async function APIResetTeamMemberPassword(requestData) {
  const requestConfig = await getRequestConfig();

  if (isDev) console.info('# APIResetTeamMemberPassword', requestData);
  else LogRocket.info('# APIResetTeamMemberPassword', requestData);

  return axios.post(resetTeamMemberPasswordURL, requestData, requestConfig);
}

export async function APIRefreshTeamMembersStatus() {
  const requestConfig = await getRequestConfig();

  return axios.post(refreshTeamMembersStatusURL, {}, requestConfig);
}

export async function APIRemoveTeamMember(requestData) {
  const requestConfig = await getRequestConfig();

  if (isDev) console.info('# APIRemoveTeamMember', requestData);
  else LogRocket.info('# APIRemoveTeamMember', requestData);

  return axios.post(removeTeamMemberURL, requestData, requestConfig);
}

/**
 * Intercom identity verification: a short-lived JWT for the signed-in user, signed by the backend.
 * Resolves to null when the backend has no Intercom secret yet, so the Messenger stays anonymous instead of
 * sending an unsigned user_id/email (which Intercom rejects once "Enforce Messenger Security" is on).
 */
export async function APIGetIntercomToken() {
  const requestConfig = await getRequestConfig();

  try {
    const { data } = await axios.post(intercomTokenURL, {}, requestConfig);
    return data && data.token ? data : null;
  } catch (error) {
    if (isDev) console.info('# APIGetIntercomToken failed', error.message);
    else LogRocket.info('# APIGetIntercomToken failed', error.message);
    return null;
  }
}

/**
 * Passkeys (WebAuthn).
 *
 * The two login calls, and the two team-invitation calls below them, are the only API calls
 * in this file made without a token — there is no session yet, that is the point of them.
 * They answer a Firebase custom token instead.
 */
export async function APIPasskeyRegisterBegin() {
  const requestConfig = await getRequestConfig();

  return axios.post(passkeyURL('registerBegin'), {}, requestConfig);
}

export async function APIPasskeyRegisterFinish(requestData) {
  const requestConfig = await getRequestConfig();

  return axios.post(passkeyURL('registerFinish'), requestData, requestConfig);
}

// A config of its own, not defaultConfig: getRequestConfig() writes the Authorization header
// into that shared object, so reusing it here would send whoever signed in last a stale token
// on a call that must not be authenticated at all.
const anonymousConfig = () => ({
  headers: { 'Content-Type': 'application/json' },
  responseType: 'json',
});

export async function APIPasskeyLoginBegin(requestData) {
  return axios.post(passkeyURL('loginBegin'), requestData, anonymousConfig());
}

export async function APIPasskeyLoginFinish(requestData) {
  return axios.post(passkeyURL('loginFinish'), requestData, anonymousConfig());
}

/**
 * Team invitations: what the /invite page shows, and the exchange that signs the person in.
 *
 * Unauthenticated, like the passkey login pair. Neither call is logged: the request body is
 * the invitation itself, and LogRocket is not a place to keep credentials. APIGetInviteInfo
 * mutates nothing on the server, which is what makes it safe for a mail scanner to prefetch
 * the page; only APIAcceptInvite spends the invitation.
 */
export async function APIGetInviteInfo(requestData) {
  return axios.post(inviteInfoURL, requestData, anonymousConfig());
}

export async function APIAcceptInvite(requestData) {
  return axios.post(acceptInviteURL, requestData, anonymousConfig());
}

/** Sends a team member their invitation again, with a fresh link; the previous one dies. */
export async function APIResendTeamInvite(requestData) {
  const requestConfig = await getRequestConfig();

  if (isDev) console.info('# APIResendTeamInvite', requestData);
  else LogRocket.info('# APIResendTeamInvite', requestData);

  return axios.post(resendTeamInviteURL, requestData, requestConfig);
}

export async function APIPasskeyList() {
  const requestConfig = await getRequestConfig();

  return axios.post(passkeyURL('list'), {}, requestConfig);
}

export async function APIPasskeyDelete(requestData) {
  const requestConfig = await getRequestConfig();

  return axios.post(passkeyURL('delete'), requestData, requestConfig);
}

export async function APIPasskeyRename(requestData) {
  const requestConfig = await getRequestConfig();

  return axios.post(passkeyURL('rename'), requestData, requestConfig);
}
